/**
 * Currency display for product prices (Fair Product card: `39$`).
 * Maps ISO codes + already-symbolic values to a display glyph.
 */

const CURRENCY_SYMBOLS: Record<string, string> = {
  USD: '$',
  EUR: '€',
  GBP: '£',
  RUB: '₽',
  BYN: 'Br',
}

/** True when value is already a glyph (`$`, `€`, `₽`), not an ISO code. */
function isCurrencyGlyph(value: string): boolean {
  if (!value) return false
  if (/^[A-Za-z]{3}$/.test(value)) return false
  return value.length <= 2 || value.startsWith('$') || value.startsWith('€')
}

/**
 * Resolve API `currency` (ISO code or glyph) to a display symbol.
 * Falls back to Intl narrowSymbol for unknown ISO codes.
 */
export function getCurrencySymbol(currency: string): string {
  const raw = currency?.trim() ?? ''
  if (!raw) return ''

  if (isCurrencyGlyph(raw)) {
    return raw
  }

  const code = raw.toUpperCase()
  if (CURRENCY_SYMBOLS[code]) {
    return CURRENCY_SYMBOLS[code]
  }

  try {
    const parts = new Intl.NumberFormat('en', {
      style: 'currency',
      currency: code,
      currencyDisplay: 'narrowSymbol',
    }).formatToParts(0)
    return parts.find((part) => part.type === 'currency')?.value ?? raw
  } catch {
    return raw
  }
}

/**
 * Format amount + currency for Fair cards.
 * Single-char symbols → suffix no space (`313.45$`, `390₽`).
 * Multi-char (`Br`) → suffix with space (`120 Br`).
 */
export function formatPriceWithCurrency(
  amount: number,
  currency: string,
): string {
  const symbol = getCurrencySymbol(currency)
  if (!symbol) {
    return String(amount)
  }
  if (symbol.length === 1) {
    return `${amount}${symbol}`
  }
  return `${amount} ${symbol}`
}
